import { useEffect, useState } from 'react';

import { api } from '@/utils/api';

/**
 * Banner displayed when the Unipile API (LinkedIn messaging service) is unavailable.
 * This typically happens when our API token is invalid/expired.
 * The banner polls every 60 seconds to check if the issue is resolved.
 */
export const UnipileStatusBanner = () => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Return null on initial render to match server-side render and prevent hydration mismatch
  if (!isMounted) {
    return null;
  }

  return <UnipileStatusBannerClient />;
};

/**
 * Client-only component that handles the actual status polling and display.
 * Separated to ensure the query only runs on the client.
 */
const UnipileStatusBannerClient = () => {
  const statusQuery = api.configs.unipileStatus.useQuery(undefined, {
    refetchInterval: 60_000, // Check every 60 seconds
    staleTime: 30_000, // Consider data stale after 30 seconds
  });

  // Don't show banner if:
  // - Loading
  // - Error occurred (fail gracefully - don't show banner)
  // - Data shows healthy status
  // - No data yet (query hasn't completed)
  if (statusQuery.isLoading || statusQuery.isError || !statusQuery.data || statusQuery.data.healthy) {
    return null;
  }

  return (
    <div className="flex items-center justify-between bg-red-500 px-4 py-2 text-white">
      <div className="flex items-center gap-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
        <span className="font-medium">
          LinkedIn messaging service is temporarily unavailable.
        </span>
        <span className="text-red-100">
          Message sending and voice notes are disabled. Our team has been notified.
        </span>
      </div>
    </div>
  );
};
